import {
  IOrganiser,
  TaskApprovalType,
  TaskStatus,
} from "@evennt-tech/api-v2-core";
import { IStandTaskListRow } from "ts/api/stand-task-list-row";
import { isExpectedOrganiser } from "./tools";

export const differenceDays = (
  firstTimestamp: number,
  secondTimestamp: number
) => {
  const result = (firstTimestamp - secondTimestamp) / 1000 / 60 / 60 / 24;
  return result;
};

/**
 * Status / due-date predicates over the row shape:
 *   - status   → `row.instance?.status ?? TODO`
 *   - dueDate  → `row.resolvedDueDate` (Date | null, computed by the hook)
 *   - approval → `row.task.approvalConfig?.type` (`TaskApprovalType` enum)
 *   - revision → `row.instance?.isInRevision` + `revisionMessage`
 *   - rejected → `row.instance?.isRejected`
 */

const rowStatus = (row: IStandTaskListRow): TaskStatus =>
  row.instance?.status ?? TaskStatus.TODO;

const isTaskInRevision = (row: IStandTaskListRow): boolean =>
  !!row.instance?.isInRevision && !!row.instance?.revisionMessage?.length;

export const isInReview = (
  row?: IStandTaskListRow,
  type?: TaskApprovalType | string,
  organiserId?: string
): boolean => {
  if (!row) return false;
  const baseCheck =
    rowStatus(row) === TaskStatus.IN_REVIEW &&
    (type ? row.task.approvalConfig?.type === type : true);
  if (organiserId) {
    return baseCheck && isExpectedOrganiser(organiserId, row, type);
  }
  return baseCheck;
};

export const isWaitingForApproval = (
  row?: IStandTaskListRow,
  type?: TaskApprovalType | string,
  organiserId?: string
): boolean => {
  if (!row) return false;
  const baseCheck =
    rowStatus(row) === TaskStatus.WAITING_FOR_APPROVAL &&
    (type ? row.task.approvalConfig?.type === type : true);
  if (organiserId) {
    return baseCheck && isExpectedOrganiser(organiserId, row, type);
  }
  return baseCheck;
};

export const isWaitingForApprovalWarning = (
  row: IStandTaskListRow
): boolean => {
  const status = rowStatus(row);
  return (
    (status === TaskStatus.WAITING_FOR_APPROVAL ||
      status === TaskStatus.IN_REVIEW) &&
    !!row.resolvedDueDate &&
    differenceDays(row.resolvedDueDate.getTime(), Date.now()) < 5
  );
};
export const isReject = (row: IStandTaskListRow): boolean => {
  return row.instance?.isRejected || false;
};

export const isWaitingOverdue = (row: IStandTaskListRow): boolean => {
  const status = rowStatus(row);
  return (
    (status === TaskStatus.WAITING_FOR_APPROVAL ||
      status === TaskStatus.IN_REVIEW) &&
    !!row.resolvedDueDate &&
    row.resolvedDueDate.getTime() < Date.now()
  );
};

export const isOverDue = (row: IStandTaskListRow): boolean =>
  rowStatus(row) === TaskStatus.TODO &&
  !isTaskInRevision(row) &&
  !!row.resolvedDueDate &&
  row.resolvedDueDate.getTime() < Date.now();

export const isTodo = (row: IStandTaskListRow): boolean => {
  const isValidDueDate = row.resolvedDueDate
    ? differenceDays(row.resolvedDueDate.getTime(), Date.now()) > 5
    : true;
  return (
    rowStatus(row) === TaskStatus.TODO &&
    isValidDueDate &&
    !row.instance?.isInRevision
  );
};

export const isTodoWarning = (row: IStandTaskListRow): boolean =>
  rowStatus(row) === TaskStatus.TODO &&
  !isTaskInRevision(row) &&
  !!row.resolvedDueDate &&
  differenceDays(row.resolvedDueDate.getTime(), Date.now()) <= 5 &&
  differenceDays(row.resolvedDueDate.getTime(), Date.now()) > 0;

export const isRevision = (row: IStandTaskListRow): boolean =>
  rowStatus(row) === TaskStatus.TODO && isTaskInRevision(row);

export const isDone = (
  row: IStandTaskListRow,
  type?: TaskApprovalType | string
): boolean => {
  const hasStatusDone = rowStatus(row) === TaskStatus.DONE;
  return type
    ? row.task.approvalConfig?.type === type && hasStatusDone
    : hasStatusDone;
};

/**
 * DRAFT detector — the instance's latest `task_responses` row is a draft
 * (`is_draft = true`), which keeps the instance in TODO, so `instance.status`
 * alone can't surface it for a simple task. Gated to TODO there so a later
 * real submit / revision / approval state always wins over a stale draft
 * flag.
 *
 * A MULTITASK parent has no response of its own — `isDraft` is an OR over
 * its subtask instances (see `_multitaskProgressByParent`), while `rowStatus`
 * reflects the PARENT's own status. A parent can legitimately already be
 * Waiting for Approval / In Review (required subtasks done) while an
 * optional subtask still has an unsaved draft, so the TODO gate must not
 * apply to multitask rows.
 */
export const isDraft = (row: IStandTaskListRow): boolean => {
  if (!row.isDraft) return false;
  if (row.multitask) return true;
  return rowStatus(row) === TaskStatus.TODO;
};

export const isUnpublished = (row: IStandTaskListRow): boolean => {
  const ud = row.task.unpublishedDate;
  if (!ud) return false;
  const t = typeof ud === "string" ? new Date(ud).getTime() : ud;
  return new Date().getTime() > Number(t);
};
